import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import { getContractApiUrl } from '../utils/apiUrls';

// Backend URL
const BACKEND_URL = getContractApiUrl();

// All contract_non_compliance columns in order
const COLUMN_ORDER = [
  'id',
  'contract_id',
  'listing_id',
  'property_address',
  'buyer_name',
  'seller_name',
  'issue_date',
  'description',
  'status',
  'resolved_at',
  'resolved_by',
  'resolution_notes',
  'created_at',
  'updated_at'
];

function DeveloperCompliance() {
  const { user, isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const [pageSize, setPageSize] = useState(20); // 10 | 20 | 50
  const [complianceIssues, setComplianceIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);
  const [resolvingId, setResolvingId] = useState(null);

  // Filters
  const [contractId, setContractId] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [sellerName, setSellerName] = useState('');
  const [dateStart, setDateStart] = useState(''); // ISO date (yyyy-mm-dd)
  const [dateEnd, setDateEnd] = useState('');
  const [openOnly, setOpenOnly] = useState(false);

  const isKeyFloUser = useMemo(() => {
    const email = user?.email || '';
    const ALLOWED_USERS = ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'];
    return ALLOWED_USERS.includes(email);
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isKeyFloUser) {
      // Redirect non-KeyFlo users back to home
      navigate('/');
    }
  }, [isAuthenticated, isKeyFloUser, navigate]);

  const formatDateTime = (ts) => {
    try {
      return new Date(ts).toLocaleString();
    } catch (e) {
      return ts || '';
    }
  };

  const formatDate = (dateStr) => {
    try {
      if (!dateStr) return '';
      // If already in YYYY-MM-DD format, return as-is (no timezone conversion)
      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
      }
      // If it's a date-only string in YYYY-MM-DD format, parse components directly to avoid timezone issues
      const dateMatch = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        return `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
      }
      // Otherwise, parse and format as YYYY-MM-DD (for other date formats)
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr || '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return dateStr || '';
    }
  };

  const buildParams = (resetOffset = true) => {
    const params = {
      limit: pageSize,
      offset: resetOffset ? 0 : offset
    };

    if (contractId) params.contract_id = contractId;
    if (buyerName) params.buyer_name = buyerName;
    if (sellerName) params.seller_name = sellerName;
    if (dateStart) params.start_date = dateStart;
    if (dateEnd) params.end_date = dateEnd;
    if (openOnly) params.status = 'open';
    return params;
  };

  const resolveIssue = async (issue) => {
    if (!window.confirm(`Resolve issue #${issue.id}?\n\n"${issue.description}"`)) return;
    setResolvingId(issue.id);
    try {
      await axios.patch(`${BACKEND_URL}/compliance-issues/${issue.id}/resolve`, {
        resolved_by: user?.email || 'manual',
      });
      setComplianceIssues((prev) =>
        prev.map((i) =>
          i.id === issue.id
            ? { ...i, status: 'resolved', resolved_at: new Date().toISOString(), resolved_by: user?.email || 'manual' }
            : i
        )
      );
    } catch (e) {
      alert(`Failed to resolve issue: ${e.response?.data?.error || e.message}`);
    } finally {
      setResolvingId(null);
    }
  };

  const fetchComplianceIssues = async (reset = true) => {
    if (!isAuthenticated || !isKeyFloUser) return;
    try {
      setLoading(true);
      setError('');

      const params = buildParams(reset);
      const response = await axios.get(`${BACKEND_URL}/compliance-issues`, { params });

      if (response.data?.status !== 'success') {
        throw new Error(response.data?.message || 'Failed to fetch compliance issues');
      }

      const newIssues = response.data.compliance_issues || [];
      setComplianceIssues(reset ? newIssues : [...complianceIssues, ...newIssues]);
      setOffset((reset ? 0 : offset) + newIssues.length);
    } catch (e) {
      setError(e.message || 'Failed to load compliance issues');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && isKeyFloUser) fetchComplianceIssues(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isKeyFloUser, pageSize, openOnly]);

  if (!isAuthenticated) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column' }}>
        <p>Please log in</p>
        <button onClick={() => loginWithRedirect({
          authorizationParams: {
            prompt: 'login'
          }
        })}>Log in</button>
      </div>
    );
  }

  if (!isKeyFloUser) {
    return (
      <div style={{ padding: '24px' }}>
        <h2>Access Denied</h2>
        <p>This page is restricted to KeyFlo team members.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2>Developer • Compliance Issues</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
          <button className="back-button" onClick={() => navigate('/')}>← Back to Contracts</button>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={openOnly}
              onChange={(e) => { setOffset(0); setOpenOnly(e.target.checked); }}
            />
            Open issues only
          </label>
          <label style={{ marginRight: '8px' }}>Page size:</label>
          <select value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value, 10))}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
          <DeveloperNavigation />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '12px', marginBottom: '12px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Contract ID</label>
          <input value={contractId} onChange={(e) => setContractId(e.target.value)} placeholder="contract_id" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Buyer Name</label>
          <input value={buyerName} onChange={(e) => setBuyerName(e.target.value)} placeholder="buyer_name" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Seller Name</label>
          <input value={sellerName} onChange={(e) => setSellerName(e.target.value)} placeholder="seller_name" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Start Date</label>
          <input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>End Date</label>
          <input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} style={{ width: '100%' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button onClick={() => { setOffset(0); fetchComplianceIssues(true); }}>Apply Filters</button>
        <button onClick={() => { setContractId(''); setBuyerName(''); setSellerName(''); setDateStart(''); setDateEnd(''); setOffset(0); fetchComplianceIssues(true); }}>Clear</button>
        <button onClick={() => fetchComplianceIssues(false)}>Load More</button>
      </div>

      {loading && <LoadingSpinner />}
      {error && (
        <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>
      )}

      <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb' }}>
        <table style={{ width: 'max-content', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto' }}>
          <thead>
            <tr>
              {COLUMN_ORDER.map((col) => (
                <th
                  key={col}
                  style={{
                    textAlign: 'left',
                    padding: '10px 12px',
                    borderBottom: '1px solid #e5e7eb',
                    background: '#f8fafc',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1
                  }}
                >
                  {col}
                </th>
              ))}
              <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', position: 'sticky', top: 0, zIndex: 1 }}>
                actions
              </th>
            </tr>
          </thead>
          <tbody>
            {complianceIssues.map((issue, idx) => (
              <tr key={issue.id || idx} style={{ borderBottom: '1px solid #f1f5f9', height: '100px', maxHeight: '100px' }}>
                {COLUMN_ORDER.map((col) => {
                  let val = issue[col];
                  if (col === 'issue_date' || col === 'resolved_at' || col === 'created_at' || col === 'updated_at') {
                    val = val ? formatDateTime(val) : '';
                  }
                  if (val === null || typeof val === 'undefined') val = '';
                  return (
                    <td
                      key={col}
                      style={{
                        padding: '10px 12px',
                        borderBottom: '1px solid #f1f5f9',
                        borderRight: '1px solid #f1f5f9',
                        verticalAlign: 'top',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        minWidth: col === 'description' ? 500 : 280
                      }}
                    >
                      <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                        {String(val)}
                      </div>
                    </td>
                  );
                })}
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top', minWidth: 100 }}>
                  {issue.status === 'open' && (
                    <button
                      onClick={() => resolveIssue(issue)}
                      disabled={resolvingId === issue.id}
                      style={{ fontSize: '0.8rem', padding: '4px 10px', cursor: 'pointer' }}
                    >
                      {resolvingId === issue.id ? '...' : 'Resolve'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {complianceIssues.length === 0 && !loading && (
              <tr>
                <td colSpan={COLUMN_ORDER.length + 1} style={{ padding: '12px', textAlign: 'center' }}>No compliance issues found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DeveloperCompliance;

